import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { getMemberDisplayName } from "@utils/data-display";
import { runtimeConfig } from "@src/runtime-config";
import { useState } from "react";
import {
  UserMenu,
  UserMenuLink,
  UserAvatarIcon,
  UserAvatar,
  IconButton,
} from "@flat-ui";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { NavbarEvents } from "@utils/track-event/track-event-list";
import { useAppNavigation } from "@router/use-app-navigation";

interface Props {
  /** The information of the current user */
  currentUser: FaroCoreApiTypes.LoggedInUser;
}

/** User menu and its dropdown */
export function UserMenuDropdown({ currentUser }: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();

  /** HTML element used to set the position of the menu. */
  const [menuAnchorElement, setMenuAnchorElement] = useState<
    HTMLElement | undefined
  >(undefined);
  const isMenuOpen = Boolean(menuAnchorElement);
  const { navigateToLogoutPage } = useAppNavigation();

  /** Basic information to display the current user */
  const userDisplayInfo = {
    email: currentUser.email,
    firstName: currentUser.firstName ?? "",
    lastName: currentUser.lastName ?? "",
    name: getMemberDisplayName(currentUser),
    profileImageUrl: currentUser.thumbnailUrl,
  };

  /** Links used at the bottom of the user menu component */
  const links: UserMenuLink[] = [
    {
      text: "Terms of service",
      href: `${runtimeConfig.urls.entryPageUrl}/legal/#terms-of-service`,
    },
    {
      text: "Privacy policy",
      href: `${runtimeConfig.urls.entryPageUrl}/legal/#privacy-policy`,
    },
    {
      text: "Imprint",
      href: "https://www.faro.com/de-DE/Imprint",
    },
  ];

  /** Trim version to remove everything after the dash */
  const trimmedVersion = runtimeConfig.appVersion.split("-")[0];

  return (
    <>
      <IconButton
        onClick={(event) => {
          setMenuAnchorElement(event.currentTarget);
          trackEvent({ name: NavbarEvents.openUserMenu });
        }}
        sx={{
          // Let the user avatar determine the size
          [">:first-of-type"]: {
            width: "auto",
            height: "auto",
          },
        }}
      >
        <UserAvatar
          userDisplayInfo={userDisplayInfo}
          icon={userDisplayInfo ? undefined : <UserAvatarIcon />}
          shouldShowGradientBorder
          size="s"
        />
      </IconButton>

      <UserMenu
        anchorElement={menuAnchorElement}
        userDisplayInfo={userDisplayInfo}
        open={isMenuOpen}
        closeMenu={() => setMenuAnchorElement(undefined)}
        appVersion={trimmedVersion}
        links={links}
        onSettingsClick={() => {
          window.open(
            `${runtimeConfig.urls.dashboardUrl}/account/security`,
            "_self"
          );
        }}
        onLogoutClick={() => {
          trackEvent({ name: NavbarEvents.logout });
          navigateToLogoutPage();
        }}
      />
    </>
  );
}
